import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { format } from 'date-fns';
import { parkingOwnerService, parkingSpaceService, bookingService } from '../firebase/services';

const OwnerDetail = () => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [parkingFee, setParkingFee] = useState('');
  const [owner, setOwner] = useState(null);
  const [parkingSpaces, setParkingSpaces] = useState([]);
  const [activeBookings, setActiveBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [allOwners, setAllOwners] = useState([]);
  const [currentOwnerIndex, setCurrentOwnerIndex] = useState(-1);

  // Load all owners for navigation
  useEffect(() => {
    const loadAllOwners = async () => {
      try {
        const db = getFirestore();
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allUsers = usersSnapshot.docs.map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));
        // Filter users who have at least 1 space
        const ownersData = allUsers.filter(user => user.spacecount && user.spacecount >= 1);
        setAllOwners(ownersData);
        
        // Find current owner index
        const index = ownersData.findIndex(o => o.id === ownerId);
        setCurrentOwnerIndex(index);
      } catch (err) {
        console.error('Error loading all owners:', err);
      }
    };

    loadAllOwners();
  }, [ownerId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && currentOwnerIndex > 0) {
        handlePrev();
      } else if (event.key === 'ArrowRight' && currentOwnerIndex < allOwners.length - 1) {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentOwnerIndex, allOwners.length]);

  // Load owner data from Firebase
  useEffect(() => {
    const loadOwnerData = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        console.log('Loading owner detail for ID:', ownerId);
        
        // Get owner data
        const ownerDoc = await getDoc(doc(db, 'users', ownerId));
        if (!ownerDoc.exists()) {
          throw new Error('Owner not found');
        }
        
        const ownerData = {
          id: ownerDoc.id,
          ...ownerDoc.data()
        };
        setOwner(ownerData);
        setParkingFee(ownerData.userfee?.toString() || '0');
        
        console.log('Owner data loaded:', ownerData);
        console.log('Owner document ID:', ownerDoc.id);
        
        // Load parking spaces for this owner
        try {
          // Get spaces using the owner document ID
          const spacesQuery = query(
            collection(db, 'spaces'),
            where('owner', '==', ownerDoc.id)
          );
          const spacesSnapshot = await getDocs(spacesQuery);
          
          console.log('Spaces query result:', spacesSnapshot.size, 'spaces found');
          console.log('Query details - owner document ID:', ownerDoc.id);
          console.log('Query details - collection: spaces');
          console.log('Query details - field: owner');
          
          if (spacesSnapshot.size > 0) {
            const spacesData = spacesSnapshot.docs.map(docSnapshot => ({
              id: docSnapshot.id,
              ...docSnapshot.data()
            }));
            setParkingSpaces(spacesData);
            console.log('Spaces data loaded:', spacesData.length, 'spaces');
            console.log('First space sample:', spacesData[0]);
          } else {
            console.log('No spaces found for this owner');
            console.log('Trying to get all spaces to see structure...');
            
            // Get all spaces to see the structure
            const allSpacesSnapshot = await getDocs(collection(db, 'spaces'));
            console.log('Total spaces in collection:', allSpacesSnapshot.size);
            
            if (allSpacesSnapshot.size > 0) {
              console.log('First 3 spaces:');
              allSpacesSnapshot.docs.slice(0, 3).forEach((docSnapshot, index) => {
                const data = docSnapshot.data();
                console.log(`Space ${index + 1}:`, { id: docSnapshot.id, ...data });
              });
            } else {
              console.log('No spaces found in spaces collection');
            }
            
            setParkingSpaces([]);
          }
        } catch (err) {
          console.log('Error loading spaces:', err);
          setParkingSpaces([]);
        }
        
        // Load active bookings for this owner
        try {
          const bookingsQuery = query(
            collection(db, 'bookings'),
            where('ownerId', '==', ownerId),
            where('status', '==', 'active')
          );
          const bookingsSnapshot = await getDocs(bookingsQuery);
          const bookingsData = bookingsSnapshot.docs.map(docSnapshot => ({
            id: docSnapshot.id,
            ...docSnapshot.data()
          }));
          setActiveBookings(bookingsData);
        } catch (err) {
          console.log('No bookings found or error loading bookings:', err);
          setActiveBookings([]);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load owner details');
        console.error('Error loading owner data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (ownerId) {
      loadOwnerData();
    }
  }, [ownerId]);

  const handleSaveFee = async () => {
    try {
      await parkingOwnerService.updateParkingFee(ownerId, parkingFee);
      alert('Parking handling fee percentage updated successfully!');
    } catch (error) {
      console.error('Error updating parking handling fee percentage:', error);
      alert('Failed to update parking handling fee percentage');
    }
  };

  const handleRemoveUser = async () => {
    if (window.confirm('Are you sure you want to remove this owner?')) {
      try {
        await parkingOwnerService.deleteOwner(ownerId);
        alert('Owner removed successfully!');
        navigate('/parking-owners-list');
      } catch (error) {
        console.error('Error removing owner:', error);
        alert('Failed to remove owner');
      }
    }
  };

  const handleAddNewSpace = () => {
    // TODO: Implement add new space functionality
    console.log('Add new space clicked');
  };

  const handleViewSpaceDetails = (space) => {
    navigate(`/space-detail/${space.id}`);
  };

  const handlePrev = () => {
    if (currentOwnerIndex > 0) {
      const prevOwner = allOwners[currentOwnerIndex - 1];
      navigate(`/owner-detail/${prevOwner.id}`);
    }
  };

  const handleNext = () => {
    if (currentOwnerIndex < allOwners.length - 1) {
      const nextOwner = allOwners[currentOwnerIndex + 1];
      navigate(`/owner-detail/${nextOwner.id}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  // Pagination for spaces
  const totalPages = Math.ceil(parkingSpaces.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentSpaces = parkingSpaces.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading owner details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !owner) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Owner not found'}</p>
            <button
              onClick={() => navigate('/parking-owners-list')}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              ← Back to Owners
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/parking-owners-list')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Owner Detail</h1>
              {allOwners.length > 0 && currentOwnerIndex >= 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Owner {currentOwnerIndex + 1} of {allOwners.length}
                  <span className="ml-2 text-xs text-gray-400">
                    (Use ← → arrow keys to navigate)
                  </span>
                </p>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleAddNewSpace}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              ADD NEW SPACE
            </button>
            <button
              onClick={handlePrev}
              disabled={currentOwnerIndex <= 0 || loading}
              className={`btn-primary flex items-center ${
                currentOwnerIndex <= 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              PREV
            </button>
            <button
              onClick={handleNext}
              disabled={currentOwnerIndex >= allOwners.length - 1 || loading}
              className={`btn-primary flex items-center ${
                currentOwnerIndex >= allOwners.length - 1 || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              NEXT
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>

        {/* Owner Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Operator Number</label>
              <p className="mt-1 text-sm text-gray-900 font-bold text-blue-600">{owner.optnum || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">First Name</label>
              <p className="mt-1 text-sm text-gray-900">{owner.firstname || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Last Name</label>
              <p className="mt-1 text-sm text-gray-900">{owner.lastname || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{owner.email || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Phone Number</label>
              <p className="mt-1 text-sm text-gray-900">{owner.phone || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Type</label>
              <p className="mt-1 text-sm text-gray-900">{owner.type || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">City</label>
              <p className="mt-1 text-sm text-gray-900">{owner.city || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">State</label>
              <p className="mt-1 text-sm text-gray-900">{owner.state || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Membership From</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(owner.created)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Number of Spaces</label>
              <p className="mt-1 text-sm text-gray-900">{owner.spacecount || 0}</p>
            </div>
          </div>
        </div>

        {/* Parking Fee Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Parking Handling Fee Percentage</h3>
          <div className="flex space-x-3">
            <input
              type="number"
              value={parkingFee}
              onChange={(e) => setParkingFee(e.target.value)}
              className="flex-1 filter-input"
              placeholder="Enter percentage (e.g., 5.5)"
              step="0.01"
              min="0"
              max="100"
            />
            <button
              onClick={handleSaveFee}
              className="btn-primary whitespace-nowrap"
            >
              Save Percentage
            </button>
          </div>
        </div>

        {/* Spaces Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Parking Spaces</h2>
            <p className="text-sm text-gray-500 mt-1">
              {parkingSpaces.length} spaces found
            </p>
          </div>

          {parkingSpaces.length > 0 ? (
            <>
              <div className="p-6 space-y-4">
                {currentSpaces.map((space) => (
                  <div key={space.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    {/* Header with Name and Status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {space.name || 'N/A'}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          space.status === 'active' 
                            ? 'bg-green-100 text-green-800'
                            : space.status === 'inactive'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {space.status === 'active' ? 'Active' : space.status === 'inactive' ? 'Inactive' : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400 font-mono">
                          ID: {space.id}
                        </span>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {space.type || 'Standard'}
                        </span>
                      </div>
                    </div>

                    {/* Description if available */}
                    {space.description && (
                      <div className="mb-3 text-sm text-gray-600">
                        {space.description}
                      </div>
                    )}

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Address:</span>
                        <div className="font-medium text-gray-900">{space.address1 || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Address 2:</span>
                        <div className="font-medium text-gray-900">{space.address2 || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">City:</span>
                        <div className="font-medium text-gray-900">{space.city || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">State:</span>
                        <div className="font-medium text-gray-900">{space.state || 'N/A'}</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleViewSpaceDetails(space)}
                        className="btn-primary text-sm"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, parkingSpaces.length)}</span> of{' '}
                        <span className="font-medium">{parkingSpaces.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500">No parking spaces found for this owner.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerDetail; 